import { Observable } from 'rxjs';
import {
  AccountMerchantRef,
  TpvMerchant,
  TpvMerchantDetail,
} from '@app/domain/models/tpv/merchant.model';
import { TpvListQuery, TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { ReadOptions } from '@app/domain/models/read-options.model';

export interface MerchantListQuery extends TpvListQuery {
  /** Estado(s) a incluir: `ACTIVO` / `INACTIVO` / `PAUSADO` / `ELIMINADO`. Se envía como CSV. */
  status?: string[];
  /** Entidad(es): `MORAL` / `FISICA`. Se envía como CSV. */
  entity?: string[];
  /** Comercios de una unidad concreta (`id_unit`). */
  idUnit?: string | number;
  /**
   * Id de la empresa dueña: PK entero de la org o UUID `id_company` (empresas
   * TPV-only sin org de Banca). Filtra el directorio a los comercios de esa
   * empresa: `GET /api/v1/tpv/merchants/?id_company=<int|uuid>` (dual-accept;
   * sustituye al viejo `?org_id_company=`, que solo aceptaba entero).
   */
  idCompany?: number | string;
  /** `true` ⇒ pide SOLO eliminados (`?deleted=true`); el backend ignora `status`. */
  deleted?: boolean;
  /**
   * Búsqueda server-side (contrato 2026-09-29): el micro TPV hace OR entre las
   * columnas visibles del directorio de comercios — nombre, razón social,
   * empresa, unidad y RFC. Sustituye al mapeo local por `merchantName →
   * ?merchant_name=`, que solo miraba el nombre y descartaba un comercio cuyo
   * RFC (o empresa/unidad) coincidía pero cuyo nombre no.
   */
  search?: string;
}

/**
 * Port de lectura del directorio de comercios.
 * `id_merchant` es OPCIONAL aquí (filtro), a diferencia de las demás lecturas.
 */
export abstract class MerchantRepository {
  /**
   * `options` (`ReadOptions`, dato PURO de dominio) va como SEGUNDO parámetro y
   * nunca dentro del query (se serializaría como parámetro de URL). Aditivo
   * (feature 152): sin él la lectura es la de siempre, sin marca ni tope.
   */
  abstract getMerchants(query?: MerchantListQuery, options?: ReadOptions): Observable<TpvPage<TpvMerchant>>;

  /**
   * Comercios de una cuenta Polipay (`accounts/<id>/merchants/`). Es el punto de
   * entrada del front: a partir de la cuenta obtiene los UUID de comercio para
   * drillear las demás lecturas. Lee el mapeo local (sin llamar al TPV).
   */
  abstract getMerchantsByAccount(polipayAccountId: number): Observable<AccountMerchantRef[]>;

  /**
   * Detalle de un comercio por su UUID (`GET merchants/<id>/`). Devuelve identidad
   * fiscal, markups y bloques de dirección/contacto/banco.
   */
  abstract getMerchantById(externalIdMerchant: string): Observable<TpvMerchantDetail>;
}
